import Dexie, { Table } from 'dexie';
import {
  CountryInterface,
  GroupsCatalog,
  OrderItemStatusInterface,
  OrderStatusInterface,
  PlantOperativeModes,
  RootCatalogInterface,
  SubGroupCatalog,
} from '@core/interfaces';
import { PlantListInterface } from '../pages/record/interfaces';
import { ICompanyModel } from '@core/models/admin/company/company.model';
import { IUserModel } from '@core/models/shared/user.model';
import { IUserRoles } from '@core/models/admin/roles/roles.model';
import { StripePrice } from '@core/models/admin-manager/stripePrice';
import { UrbanArea, Region } from '@shared/interfaces/certify/urban-area';
import { IdbSvgAssetEntry } from './idb.types';
import { IdbMenusEntry, IdbApplicationsEntry, IdbAuthSessionEntry, IdbCompanyRegionsEntry } from './idb.types';
import { PaymentTerms } from '../pages/invoice/pre-invoice/order-information/models/payment-terms.model';
import { salesChannel } from '../pages/orders/interfaces/salesChannel.interface';
import { iItemsTypes } from '../pages/orders/order-taking/components/new-order/models/iItemsTypes.model';
import { IBusinessPlanDetailResponse } from '../store/business-plant-detail/business-plant-detail.models';

import { otcCatalog } from '../pages/orders/interfaces/oneTimeCustomer.interface';
import { newOrderSelect } from '../pages/orders/interfaces/newOrderSelect.interface';
import { AditionalService } from '../pages/orders/interfaces/additionalService.interface';
import { IdbCountryReceiverEntry, IdbOtDefaultDataEntry } from './idb.types';

export interface IdbParameterDataEntry {
  key: string;
  parameterCode: string;
  parameterValue: string;
  scopeId: number | null;
  parameterScopeTypeId: number;
}

export class AppDB extends Dexie {
  countryCatalog: Table<CountryInterface, number>;
  orderItemStatusCatalog: Table<OrderItemStatusInterface, number>;
  orderStatusCatalog: Table<OrderStatusInterface, number>;
  plantOperativeModes: Table<PlantOperativeModes, number>;
  plants: Table<PlantListInterface, number>;
  equipmentCategories: Table<RootCatalogInterface, number>;
  vehicleTypes: Table<RootCatalogInterface, number>;
  units: Table<RootCatalogInterface, number>;
  gpsProviders: Table<RootCatalogInterface, number>;
  vehicleStatus: Table<RootCatalogInterface, number>;
  groups: Table<GroupsCatalog, number>;
  subGroups: Table<SubGroupCatalog, number>;
  companyInfo: Table<ICompanyModel, number>;
  accountInfo: Table<IUserModel, string>;
  userRoles: Table<IUserRoles, number>;
  stripePrices: Table<StripePrice, number>;
  parametersData: Table<IdbParameterDataEntry, string>;
  areas: Table<UrbanArea, number>;
  regions: Table<Region, number>;
  svgAssets: Table<IdbSvgAssetEntry, string>;
  plantsFull: Table<PlantListInterface, number>;
  menus: Table<IdbMenusEntry, string>;
  applications: Table<IdbApplicationsEntry, string>;
  authSessions: Table<IdbAuthSessionEntry, string>;
  companyRegions: Table<IdbCompanyRegionsEntry, string>;
  businessPlantDetails: Table<IBusinessPlanDetailResponse, string>;

  // OT Performance — 10 catalog tables
  otPaymentTerms: Table<PaymentTerms, number>;
  otSalesChannels: Table<salesChannel, number>;
  otOrderItemTypes: Table<iItemsTypes, number>;
  otTaxClasses: Table<otcCatalog, number>;
  otCashPaymentTypes: Table<otcCatalog, string>;
  otDeliveryMethods: Table<newOrderSelect, string>;
  otCountriesReceivers: Table<IdbCountryReceiverEntry, number>;
  otOrderStatuses: Table<newOrderSelect, string>;
  otOrderDefaultData: Table<IdbOtDefaultDataEntry, string>;
  otAdditionalServices: Table<AditionalService, number>;

  constructor() {
    super('ARKIK');
    this.version(4).stores({
      countryCatalog: 'CountryId,*countryCatalog',
      orderItemStatusCatalog: 'OrderItemStatusId,*orderItemStatusCatalog',
      orderStatusCatalog: 'OrderStatusId,*orderStatusCatalog',
      plantOperativeModes: 'PlantOperativeModeId,*plantOperativeModes',
      plants: 'PlantId,*plants',
      equipmentCategories: 'Id,*equipmentCategories',
      vehicleTypes: 'Id,*vehicleTypes',
      units: 'Id,*units',
      gpsProviders: 'Id,*gpsProviders',
      vehicleStatus: 'Id,*vehicleStatus',
      groups: 'GroupId,*groups',
      subGroups: 'SubGroupId,*subGroups',
      companyInfo: 'CompanyGuid,*companyInfo',
      accountInfo: 'UserGuid',
      userRoles: 'RoleGuid,*userRoles',
      stripePrices: 'StripePriceId, stripePrices',
    });

    this.version(6).stores({
      countryCatalog: 'CountryId',
      orderItemStatusCatalog: 'OrderItemStatusId',
      orderStatusCatalog: 'OrderStatusId',
      plantOperativeModes: 'PlantOperativeModeId',
      plants: 'PlantId',
      equipmentCategories: 'Id',
      vehicleTypes: 'Id',
      units: 'Id',
      gpsProviders: 'Id',
      vehicleStatus: 'Id',
      groups: 'GroupId',
      subGroups: 'SubGroupId',
      companyInfo: 'CompanyGuid',
      accountInfo: 'UserGuid',
      userRoles: 'RoleGuid',
      stripePrices: 'StripePriceId',
      parametersData: 'key',
      areas: 'urbanAreaId',
      regions: 'regionId',
    });

    // Force migration: add version 7 with full latest schema
    this.version(7).stores({
      countryCatalog: 'CountryId',
      orderItemStatusCatalog: 'OrderItemStatusId',
      orderStatusCatalog: 'OrderStatusId',
      plantOperativeModes: 'PlantOperativeModeId',
      plants: 'PlantId',
      equipmentCategories: 'Id',
      vehicleTypes: 'Id',
      units: 'Id',
      gpsProviders: 'Id',
      vehicleStatus: 'Id',
      groups: 'GroupId',
      subGroups: 'SubGroupId',
      companyInfo: 'CompanyGuid',
      accountInfo: 'UserGuid',
      userRoles: 'RoleGuid',
      stripePrices: 'StripePriceId',
      parametersData: 'key',
      areas: 'urbanAreaId',
      regions: 'regionId',
    });

    this.version(6).stores({
      countryCatalog: 'CountryId',
      orderItemStatusCatalog: 'OrderItemStatusId',
      orderStatusCatalog: 'OrderStatusId',
      plantOperativeModes: 'PlantOperativeModeId',
      plants: 'PlantId',
      equipmentCategories: 'Id',
      vehicleTypes: 'Id',
      units: 'Id',
      gpsProviders: 'Id',
      vehicleStatus: 'Id',
      groups: 'GroupId',
      subGroups: 'SubGroupId',
      companyInfo: 'CompanyGuid',
      accountInfo: 'UserGuid',
      userRoles: 'RoleGuid',
      stripePrices: 'StripePriceId',
      parametersData: 'key',
      areas: 'urbanAreaId',
      regions: 'regionId',
    });

    // Force migration: add version 7 with full latest schema
    this.version(7).stores({
      countryCatalog: 'CountryId',
      orderItemStatusCatalog: 'OrderItemStatusId',
      orderStatusCatalog: 'OrderStatusId',
      plantOperativeModes: 'PlantOperativeModeId',
      plants: 'PlantId',
      equipmentCategories: 'Id',
      vehicleTypes: 'Id',
      units: 'Id',
      gpsProviders: 'Id',
      vehicleStatus: 'Id',
      groups: 'GroupId',
      subGroups: 'SubGroupId',
      companyInfo: 'CompanyGuid',
      accountInfo: 'UserGuid',
      userRoles: 'RoleGuid',
      stripePrices: 'StripePriceId',
      parametersData: 'key',
      areas: 'urbanAreaId',
      regions: 'regionId',
    });

    // Final version with SVG assets table
    this.version(8).stores({
      countryCatalog: 'CountryId',
      orderItemStatusCatalog: 'OrderItemStatusId',
      orderStatusCatalog: 'OrderStatusId',
      plantOperativeModes: 'PlantOperativeModeId',
      plants: 'PlantId',
      equipmentCategories: 'Id',
      vehicleTypes: 'Id',
      units: 'Id',
      gpsProviders: 'Id',
      vehicleStatus: 'Id',
      groups: 'GroupId',
      subGroups: 'SubGroupId',
      companyInfo: 'CompanyGuid',
      accountInfo: 'UserGuid',
      userRoles: 'RoleGuid',
      stripePrices: 'StripePriceId',
      parametersData: 'key',
      areas: 'urbanAreaId',
      regions: 'regionId',
      svgAssets: 'url',
    });

    // OT Performance: 10 catalog tables for Order Taking pre-load
    this.version(9).stores({
      countryCatalog: 'CountryId',
      orderItemStatusCatalog: 'OrderItemStatusId',
      orderStatusCatalog: 'OrderStatusId',
      plantOperativeModes: 'PlantOperativeModeId',
      plants: 'PlantId',
      equipmentCategories: 'Id',
      vehicleTypes: 'Id',
      units: 'Id',
      gpsProviders: 'Id',
      vehicleStatus: 'Id',
      groups: 'GroupId',
      subGroups: 'SubGroupId',
      companyInfo: 'CompanyGuid',
      accountInfo: 'UserGuid',
      userRoles: 'RoleGuid',
      stripePrices: 'StripePriceId',
      parametersData: 'key',
      areas: 'urbanAreaId',
      regions: 'regionId',
      svgAssets: 'url',
      otPaymentTerms: 'paymentTermId',
      otSalesChannels: 'channelId',
      otOrderItemTypes: 'orderItemTypeId',
      otTaxClasses: 'id',
      otCashPaymentTypes: 'id',
      otDeliveryMethods: 'value',
      otCountriesReceivers: 'countryId',
      otOrderStatuses: 'value',
      otOrderDefaultData: 'key',
      otAdditionalServices: 'productId',
    });

    this.version(6).stores({
      countryCatalog: 'CountryId',
      orderItemStatusCatalog: 'OrderItemStatusId',
      orderStatusCatalog: 'OrderStatusId',
      plantOperativeModes: 'PlantOperativeModeId',
      plants: 'PlantId',
      equipmentCategories: 'Id',
      vehicleTypes: 'Id',
      units: 'Id',
      gpsProviders: 'Id',
      vehicleStatus: 'Id',
      groups: 'GroupId',
      subGroups: 'SubGroupId',
      companyInfo: 'CompanyGuid',
      accountInfo: 'UserGuid',
      userRoles: 'RoleGuid',
      stripePrices: 'StripePriceId',
      parametersData: 'key',
      areas: 'urbanAreaId',
      regions: 'regionId',
    });

    // Force migration: add version 7 with full latest schema
    this.version(7).stores({
      countryCatalog: 'CountryId',
      orderItemStatusCatalog: 'OrderItemStatusId',
      orderStatusCatalog: 'OrderStatusId',
      plantOperativeModes: 'PlantOperativeModeId',
      plants: 'PlantId',
      equipmentCategories: 'Id',
      vehicleTypes: 'Id',
      units: 'Id',
      gpsProviders: 'Id',
      vehicleStatus: 'Id',
      groups: 'GroupId',
      subGroups: 'SubGroupId',
      companyInfo: 'CompanyGuid',
      accountInfo: 'UserGuid',
      userRoles: 'RoleGuid',
      stripePrices: 'StripePriceId',
      parametersData: 'key',
      areas: 'urbanAreaId',
      regions: 'regionId',
    });

    // Final version with SVG assets table
    this.version(8).stores({
      countryCatalog: 'CountryId',
      orderItemStatusCatalog: 'OrderItemStatusId',
      orderStatusCatalog: 'OrderStatusId',
      plantOperativeModes: 'PlantOperativeModeId',
      plants: 'PlantId',
      equipmentCategories: 'Id',
      vehicleTypes: 'Id',
      units: 'Id',
      gpsProviders: 'Id',
      vehicleStatus: 'Id',
      groups: 'GroupId',
      subGroups: 'SubGroupId',
      companyInfo: 'CompanyGuid',
      accountInfo: 'UserGuid',
      userRoles: 'RoleGuid',
      stripePrices: 'StripePriceId',
      parametersData: 'key',
      areas: 'urbanAreaId',
      regions: 'regionId',
      svgAssets: 'url',
    });

    // OT Performance: 10 catalog tables for Order Taking pre-load
    this.version(9).stores({
      countryCatalog: 'CountryId',
      orderItemStatusCatalog: 'OrderItemStatusId',
      orderStatusCatalog: 'OrderStatusId',
      plantOperativeModes: 'PlantOperativeModeId',
      plants: 'PlantId',
      equipmentCategories: 'Id',
      vehicleTypes: 'Id',
      units: 'Id',
      gpsProviders: 'Id',
      vehicleStatus: 'Id',
      groups: 'GroupId',
      subGroups: 'SubGroupId',
      companyInfo: 'CompanyGuid',
      accountInfo: 'UserGuid',
      userRoles: 'RoleGuid',
      stripePrices: 'StripePriceId',
      parametersData: 'key',
      areas: 'urbanAreaId',
      regions: 'regionId',
      svgAssets: 'url',
      otPaymentTerms: 'paymentTermId',
      otSalesChannels: 'channelId',
      otOrderItemTypes: 'orderItemTypeId',
      otTaxClasses: 'id',
      otCashPaymentTypes: 'id',
      otDeliveryMethods: 'value',
      otCountriesReceivers: 'countryId',
      otOrderStatuses: 'value',
      otOrderDefaultData: 'key',
      otAdditionalServices: 'productId',
    });

    // Cache-first dashboard: full plants list (unfiltered) + menus-by-user singleton
    this.version(10).stores({
      countryCatalog: 'CountryId',
      orderItemStatusCatalog: 'OrderItemStatusId',
      orderStatusCatalog: 'OrderStatusId',
      plantOperativeModes: 'PlantOperativeModeId',
      plants: 'PlantId',
      equipmentCategories: 'Id',
      vehicleTypes: 'Id',
      units: 'Id',
      gpsProviders: 'Id',
      vehicleStatus: 'Id',
      groups: 'GroupId',
      subGroups: 'SubGroupId',
      companyInfo: 'CompanyGuid',
      accountInfo: 'UserGuid',
      userRoles: 'RoleGuid',
      stripePrices: 'StripePriceId',
      parametersData: 'key',
      areas: 'urbanAreaId',
      regions: 'regionId',
      svgAssets: 'url',
      otPaymentTerms: 'paymentTermId',
      otSalesChannels: 'channelId',
      otOrderItemTypes: 'orderItemTypeId',
      otTaxClasses: 'id',
      otCashPaymentTypes: 'id',
      otDeliveryMethods: 'value',
      otCountriesReceivers: 'countryId',
      otOrderStatuses: 'value',
      otOrderDefaultData: 'key',
      otAdditionalServices: 'productId',
      plantsFull: 'PlantId',
      menus: 'key',
    });

    // Cache-first dashboard: applications-by-company singleton
    this.version(11).stores({
      countryCatalog: 'CountryId',
      orderItemStatusCatalog: 'OrderItemStatusId',
      orderStatusCatalog: 'OrderStatusId',
      plantOperativeModes: 'PlantOperativeModeId',
      plants: 'PlantId',
      equipmentCategories: 'Id',
      vehicleTypes: 'Id',
      units: 'Id',
      gpsProviders: 'Id',
      vehicleStatus: 'Id',
      groups: 'GroupId',
      subGroups: 'SubGroupId',
      companyInfo: 'CompanyGuid',
      accountInfo: 'UserGuid',
      userRoles: 'RoleGuid',
      stripePrices: 'StripePriceId',
      parametersData: 'key',
      areas: 'urbanAreaId',
      regions: 'regionId',
      svgAssets: 'url',
      otPaymentTerms: 'paymentTermId',
      otSalesChannels: 'channelId',
      otOrderItemTypes: 'orderItemTypeId',
      otTaxClasses: 'id',
      otCashPaymentTypes: 'id',
      otDeliveryMethods: 'value',
      otCountriesReceivers: 'countryId',
      otOrderStatuses: 'value',
      otOrderDefaultData: 'key',
      otAdditionalServices: 'productId',
      plantsFull: 'PlantId',
      menus: 'key',
      applications: 'key',
    });

    // Cache-first login: static client/market identifiers keyed by email
    this.version(12).stores({
      countryCatalog: 'CountryId',
      orderItemStatusCatalog: 'OrderItemStatusId',
      orderStatusCatalog: 'OrderStatusId',
      plantOperativeModes: 'PlantOperativeModeId',
      plants: 'PlantId',
      equipmentCategories: 'Id',
      vehicleTypes: 'Id',
      units: 'Id',
      gpsProviders: 'Id',
      vehicleStatus: 'Id',
      groups: 'GroupId',
      subGroups: 'SubGroupId',
      companyInfo: 'CompanyGuid',
      accountInfo: 'UserGuid',
      userRoles: 'RoleGuid',
      stripePrices: 'StripePriceId',
      parametersData: 'key',
      areas: 'urbanAreaId',
      regions: 'regionId',
      svgAssets: 'url',
      otPaymentTerms: 'paymentTermId',
      otSalesChannels: 'channelId',
      otOrderItemTypes: 'orderItemTypeId',
      otTaxClasses: 'id',
      otCashPaymentTypes: 'id',
      otDeliveryMethods: 'value',
      otCountriesReceivers: 'countryId',
      otOrderStatuses: 'value',
      otOrderDefaultData: 'key',
      otAdditionalServices: 'productId',
      plantsFull: 'PlantId',
      menus: 'key',
      applications: 'key',
      authSessions: 'email',
    });

    // Cache-first tenant regions (companies-by-user, ProfilerService)
    this.version(13).stores({
      countryCatalog: 'CountryId',
      orderItemStatusCatalog: 'OrderItemStatusId',
      orderStatusCatalog: 'OrderStatusId',
      plantOperativeModes: 'PlantOperativeModeId',
      plants: 'PlantId',
      equipmentCategories: 'Id',
      vehicleTypes: 'Id',
      units: 'Id',
      gpsProviders: 'Id',
      vehicleStatus: 'Id',
      groups: 'GroupId',
      subGroups: 'SubGroupId',
      companyInfo: 'CompanyGuid',
      accountInfo: 'UserGuid',
      userRoles: 'RoleGuid',
      stripePrices: 'StripePriceId',
      parametersData: 'key',
      areas: 'urbanAreaId',
      regions: 'regionId',
      svgAssets: 'url',
      otPaymentTerms: 'paymentTermId',
      otSalesChannels: 'channelId',
      otOrderItemTypes: 'orderItemTypeId',
      otTaxClasses: 'id',
      otCashPaymentTypes: 'id',
      otDeliveryMethods: 'value',
      otCountriesReceivers: 'countryId',
      otOrderStatuses: 'value',
      otOrderDefaultData: 'key',
      otAdditionalServices: 'productId',
      plantsFull: 'PlantId',
      menus: 'key',
      applications: 'key',
      authSessions: 'email',
      companyRegions: 'key',
    });

    this.version(14).stores({
      countryCatalog: 'CountryId',
      orderItemStatusCatalog: 'OrderItemStatusId',
      orderStatusCatalog: 'OrderStatusId',
      plantOperativeModes: 'PlantOperativeModeId',
      plants: 'PlantId',
      equipmentCategories: 'Id',
      vehicleTypes: 'Id',
      units: 'Id',
      gpsProviders: 'Id',
      vehicleStatus: 'Id',
      groups: 'GroupId',
      subGroups: 'SubGroupId',
      companyInfo: 'CompanyGuid',
      accountInfo: 'UserGuid',
      userRoles: 'RoleGuid',
      stripePrices: 'StripePriceId',
      parametersData: 'key',
      areas: 'urbanAreaId',
      regions: 'regionId',
      svgAssets: 'url',
      otPaymentTerms: 'paymentTermId',
      otSalesChannels: 'channelId',
      otOrderItemTypes: 'orderItemTypeId',
      otTaxClasses: 'id',
      otCashPaymentTypes: 'id',
      otDeliveryMethods: 'value',
      otCountriesReceivers: 'countryId',
      otOrderStatuses: 'value',
      otOrderDefaultData: 'key',
      otAdditionalServices: 'productId',
      plantsFull: 'PlantId',
      menus: 'key',
      applications: 'key',
      authSessions: 'email',
      companyRegions: 'key',
      businessPlantDetails: 'BusinessPlanGuid',
    });
  }

  public async clear(): Promise<void> {
    await Promise.all(this.tables.map((table) => table.clear()));
  }
}
